import React from "react";
import { Redirect } from "react-router-dom";
import { Checkbox, Form, Grid } from "semantic-ui-react";
import styled from "styled-components";
import { Constants, ErrorMessages, Paths } from "../Constants";
import { isEmailFormatValid, isPasswordFormatValid } from "../util/regexHelper";
import stringHelper from "../util/stringHelper";
import BaseComponent from "./BaseComponent";
import AppHeaderSmall from "./common/AppHeaderSmall";
import Footer from "./common/Footer";
import MainContentWrapper from "./common/MainContentWrapper";
import PillButton from "./common/PillButton";
import SFCookieConsent from "./common/SFCookieConsent";
import SFLoader from "./common/SFLoader";
import SiteWrapper from "./common/SiteWrapper";
import ErrorDisplay from "./ErrorDisplay";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const StyledForm = styled(Form)`
  width: 400px;
  margin-top: 80px;
`;

const CenteredButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

class Registration extends BaseComponent {
  constructor() {
    super();
    this.state = {
      email: null,
      password: null,
      passwordConfirm: null,
      emailValid: false,
      emailChanged: false,
      passwordsValid: false,
      passwordsEntered: false,
      nextScreen: null,
      showActivityIndicator: false,
      termsOfService: false,
      privacyPolicy: false,
      redirectPath: null,
    };

    this.register = this.register.bind(this);
    this.validateEntry = this.validateEntry.bind(this);
    this.checkEmail = this.checkEmail.bind(this);
    this.isCreateButtonEnabled = this.isCreateButtonEnabled.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onTermsChanged = this.onTermsChanged.bind(this);
    this.onPrivacyChanged = this.onPrivacyChanged.bind(this);
    this.checkConfirmPassword = this.checkConfirmPassword.bind(this);
  }

  async register() {
    const email = this.state.email;
    const password = this.state.password;

    this.errorManager.reset();

    if (stringHelper.isNullOrEmpty(email)) {
      this.errorManager.addError("You must enter your email to register");
      this.forceUpdate();
      return;
    }
    if (stringHelper.isNullOrEmpty(password)) {
      this.errorManager.addError("You must enter a password to register");
      this.forceUpdate();
      return;
    }

    this.setState({ showActivityIndicator: true });
    this.forceUpdate();

    const trimmedEmail = String.prototype.trim.call(email);
    let response;

    try {
      response = await this.identityApi.register(trimmedEmail, password);
    } catch (err) {
      console.log(err);
      if (err.response) {
        switch (err.response.status) {
          case 500:
            this.errorManager.addError(
              "Unexpected server error while registering user"
            );
            break;
          default:
            this.errorManager.addError(
              "Unknown error while registering user: " + err
            );
        }
        this.errorManager.addError(err.response.data);
      }
      this.setState({ showActivityIndicator: false });
      return;
    }

    if (!response.data.succeeded) {
      this.errorManager.addCodedErrors(response.data.errors);
      this.setState({ showActivityIndicator: false });
      return;
    }

    try {
      response = await this.identityApi.loginUser(trimmedEmail, password);
    } catch (err) {
      console.log(err);
      if (err.response) {
        switch (err.response.status) {
          case 500:
            this.errorManager.addError(
              "Failed to login user after registration."
            );
            break;
          case 400:
            console.log(err.response);
            this.errorManager.addCodedError(
              err.response.data.error,
              err.response.data.error_description
            );
            break;
          default:
            this.errorManager.addError(
              "Failed to login user after registration. " + err
            );
        }
      }
      this.setState({ showActivityIndicator: false });
      return;
    }

    try {
      response = await this.identityApi.sendConfirmationEmail(trimmedEmail);
      if (!response) {
        this.errorManager.addError("Failed to send confirmation email.");
        this.forceUpdate();
        return;
      }
    } catch (err) {
      console.log(err);
      if (err.response) {
        switch (err.response.status) {
          case 500:
            this.errorManager.addError("Failed to send confirmation email.");
            break;
          default:
            this.errorManager.addError("Failed to send confirmation email.");
        }
      }
      console.log(err);
      this.setState({ showActivityIndicator: false });
      return;
    }

    this.setState({
      showActivityIndicator: false,
      redirectPath: Paths.CHECK_CONFIRMATION,
    });
  }

  async checkEmail() {
    try {
      this.errorManager.reset();

      //Quick out to avoid error message for null or empty string
      if (stringHelper.isNullOrEmpty(this.state.email)) {
        this.setState({ emailValid: false });
        return;
      }

      //check first with regular expression
      if (!isEmailFormatValid(this.state.email)) {
        this.errorManager.addError("Please enter a valid email address.");
        this.setState({ emailValid: false });
        return;
      }

      this.setState({ showActivityIndicator: true });
      let response = await this.identityApi.validateRequestedEmail(
        this.state.email
      );

      if (!response.data.available) {
        this.errorManager.addError(
          "That email address is not available. To login with your existing account, please hit 'Back' then 'Sign In'."
        );
        this.setState({ showActivityIndicator: false, emailValid: false });
        return;
      }
      if (!response.data.valid) {
        this.errorManager.addError("Please enter a valid email address.");
        this.setState({ showActivityIndicator: false, emailValid: false });
        return;
      }
      this.setState({ emailValid: true, showActivityIndicator: false });
    } catch (err) {
      console.log(err);
      this.errorManager.addError(
        "There was an error validating the email address."
      );
      this.setState({ showActivityIndicator: false });
      return;
    }
  }

  validateEntry() {
    this.errorManager.reset();
    //See if each input has something
    if (!this.state.emailValid) {
      return;
    }

    if (
      stringHelper.isNullOrEmpty(this.state.email) ||
      stringHelper.isNullOrEmpty(this.state.password) ||
      stringHelper.isNullOrEmpty(this.state.passwordConfirm)
    ) {
      return;
    } else {
      this.setState({ passwordsEntered: true });

      if (this.state.password !== this.state.passwordConfirm) {
        this.errorManager.addError("Passwords do not match.");
        this.setState({ passwordsValid: false });
        return;
      }

      if (this.state.password.length < 8) {
        this.errorManager.addError(
          "Passwords must be at least 8 characters long."
        );
        this.setState({ passwordsValid: false });
        return;
      }

      //need to test for certain characters - really need regex at this point
      if (!isPasswordFormatValid(this.state.password)) {
        this.errorManager.addError(ErrorMessages.PASSWORD_FORMAT);
        this.setState({ passwordsValid: false });
        return;
      }

      this.setState({ passwordsValid: true });
    }
  }

  isCreateButtonEnabled() {
    const result =
      this.state.emailValid &&
      this.state.passwordsEntered &&
      this.state.password === this.state.passwordConfirm &&
      this.state.passwordConfirm.length >= 8 &&
      isPasswordFormatValid(this.state.password) &&
      !this.state.showActivityIndicator &&
      this.state.termsOfService &&
      this.state.privacyPolicy;
    //console.log(result);
    return result;
  }

  onSubmit() {
    // call service here..
    console.log("state: ", this.state);
  }

  onTermsChanged(event, data) {
    this.setState({ acceptTermsOfService: data.checked });
  }

  onPrivacyChanged(event, data) {
    this.setState({ acceptPrivacyPolicy: data.checked });
  }

  checkConfirmPassword(value) {
    if (this.state.password === value) {
      this.validateEntry();
    }
  }

  render() {
    return (
      <SiteWrapper>
        {this.state.redirectPath ? (
          <Redirect
            to={{
              pathname: this.state.redirectPath,
              search: this.state.redirectSearch,
            }}
          />
        ) : null}

        <AppHeaderSmall title="Registration" />
        <SFLoader active={this.state.showActivityIndicator} />
        <MainContentWrapper>
          <Wrapper>
            <StyledForm>
              <Form.Input
                onChange={(e) => {
                  this.setState({ email: e.target.value });
                }}
                onBlur={(e) => {
                  this.checkEmail();
                }}
                placeholder="Enter your email"
              />
              <Form.Input
                onChange={(e) => {
                  this.setState({ password: e.target.value });
                }}
                onBlur={(e) => {
                  this.validateEntry();
                }}
                placeholder="Enter your password"
                autoomplete="off"
                type="password"
              />
              <Form.Input
                onChange={(e) => {
                  this.checkConfirmPassword(e.target.value);
                  this.setState({ passwordConfirm: e.target.value });
                }}
                onBlur={(e) => {
                  this.validateEntry();
                }}
                placeholder="Confirm your password"
                type="password"
                autoomplete="off"
              />

              <Grid columns={1}>
                <Grid.Row>
                  <Grid.Column>
                    Password must contain at least one upper case letter, one
                    lower case letter, one number, and one special character.
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Checkbox
                      toggle
                      checked={this.state.acceptTermsOfService}
                      onChange={this.onTermsChanged}
                      label={
                        <label>
                          I accept the{" "}
                          <a href={Constants.TOS_URL}>Terms of Service</a>
                        </label>
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Checkbox
                      toggle
                      checked={this.state.acceptPrivacyPolicy}
                      onChange={this.onPrivacyChanged}
                      label={
                        <label>
                          I accept the{" "}
                          <a href={Constants.PRIVACY_POLICY_URL}>
                            Privacy Policy
                          </a>
                        </label>
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
                {this.errorManager.getErrorCount() > 0 ? (
                  <Grid.Row>
                    <Grid.Column>
                      <ErrorDisplay errorManager={this.errorManager} />
                    </Grid.Column>
                  </Grid.Row>
                ) : null}
                <Grid.Row>
                  <Grid.Column>
                    <CenteredButtonWrapper>
                      <PillButton
                        type="submit"
                        primary
                        style={{ width: "200px" }}
                        onClick={this.register}
                        disabled={
                          !this.state.passwordsValid ||
                          !this.state.acceptTermsOfService ||
                          !this.state.acceptPrivacyPolicy
                        }
                      >
                        Create Account
                      </PillButton>
                    </CenteredButtonWrapper>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </StyledForm>
          </Wrapper>
        </MainContentWrapper>
        <Footer />
        <SFCookieConsent />
      </SiteWrapper>
    );
  }
}

export default Registration;
